import { boundsOfValue, type Evaluated } from '@/domain/construction';
import { bounds as boundsOps, type Bounds } from '@/domain/geometry';

/**
 * The world rectangle that must contain a value's **pick target**.
 *
 * ## Why this is not `boundsOfValue`
 *
 * `boundsOfValue` answers "what does this occupy", which is the question export framing asks.
 * Picking asks a different one: "where might a click land on this". For almost every value
 * those coincide, and for one they do not — an angle mark is bounded at its vertex but is
 * *picked on its arc*, deliberately, so that marking a right angle does not make the vertex
 * undraggable.
 *
 * Indexing by the wrong one of the two makes the mark unselectable: the index is asked about
 * the arc, the mark is filed under the vertex, and it is simply never offered as a candidate.
 * That is the failure mode a spatial index has to be built against — it does not throw, it
 * quietly loses things — which is why the index is fed from here and why a property test
 * checks every indexed query against a brute-force sweep.
 *
 * The rule for anything indexed: **this must be a superset of where a hit can be found.**
 * Too large only costs a distance computation; too small loses the entity.
 */
export function pickBoundsOf(value: Evaluated): Bounds | null {
  const box = boundsOfValue(value);
  if (box === null) return null;

  if (value.kind !== 'angle-mark') return box;

  /*
   * The pick point sits on the arc, and the arc is never further from the vertex than a third
   * of the shorter arm. Expanding by the *whole* arm is therefore comfortably a superset, which
   * is the rule above — and it leaves room for the value label as well. Deliberately not
   * `angleMarkRadius`: this is the one place where being too generous is free and being exact
   * would put the index one rounding error away from losing an entity.
   */
  const reach = Math.max(value.armLength, 1);
  return boundsOps.bounds(box.minX - reach, box.minY - reach, box.maxX + reach, box.maxY + reach);
}
